import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DateIntervalFilterData } from '../../../../_shared/models';
import { PaginationRequest } from '../../../../_shared/models/pagination-request.model';
import { endOfDay, startOfDay } from 'date-fns';
import { BaseWidgetComponent } from '../../../../home/home-widgets/widgets/base-widget.component';
import { DriverOperationsService, FilterService } from '../../../../_shared/services';
import { OperationStatusDetail } from '../../models/operation-status-detail.model';
import { FormsModule } from '@angular/forms';
import { TruncatetextPipe } from '../../../../_shared/pipes/truncatetext.pipe';
import { AvatarComponent } from "../../../../_shared/components/avatar/avatar.component";
import { TimeStatusIndicatorComponent } from "../../../../_shared/components/time-status-indicator/time-status-indicator.component";
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from "../../../../_shared/components/pagination/pagination.component";
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { getDriverStatusLabelKey } from '../../helpers/driver-status-label.helper';

@Component({
  selector: 'app-driver-overview',
  standalone: true,
  imports: [RouterModule, FormsModule, TruncatetextPipe, NgbTooltip, AvatarComponent, TimeStatusIndicatorComponent, PaginationComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-overview.component.html',
  styleUrl: './driver-overview.component.scss',
})
export class DriverOverviewComponent extends BaseWidgetComponent implements OnInit {
  private driverOperationsService = inject(DriverOperationsService);
  private errorHandlerService = inject(ErrorHandlerService);

  public dateFilter: DateIntervalFilterData;
  public drivers: any = [];
  public totalItems: number = 0;
  public currentItems: number = 0;
  public numberOfPages: number = 0;
  public currentPage: number = 1;
  public override isLoading: boolean = true;
  public searchTerm: string = '';
  public filteredDriversData: any[] = [];

  currentDate: Date = new Date();
  paginatedDateFilterRequest: PaginationRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    page: 1,
    pageSize: 10,
    searchTerm: ''
  };

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.dateFilter = {} as DateIntervalFilterData;
  }

  ngOnInit(): void {
    this.getDriverStatusDetailAsync()
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.paginatedDateFilterRequest.accountIds = this.widgetFilter.accountIds;
    await this.getDriverStatusDetailAsync();
  }

  public override formatValue(value: number): string {
    return `${value.toLocaleString()}`;
  }

  async createPagination(page: number) {
    if (page > this.numberOfPages || page < 1)
      return;

    this.paginatedDateFilterRequest.page = page;
    this.currentPage = page;

    await this.getDriverStatusDetailAsync();
  }

  private async getDriverStatusDetailAsync() {
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const response = await this.driverOperationsService.getDriverStatusDetail(this.paginatedDateFilterRequest, this.cancelSubject);
      this.totalItems = response.data?.totalCount ?? 0;
      this.currentItems = Math.min(this.currentPage * this.paginatedDateFilterRequest.pageSize, this.totalItems);
      this.numberOfPages = response.data?.totalPages ?? 0;

      this.drivers = response.data?.results.map((driver: OperationStatusDetail) => ({
        name: `${driver.firstName} ${driver.lastName}`,
        vehicle: driver.assetName,
        image: driver?.profilePictureUrl,
        signIn: { time: driver?.signIn ? driver.signIn.toString() : 'N/A', status: 'warning' },
        inVehicle: { time: driver?.inVehicle ? driver.inVehicle.toString() : 'N/A', status: 'success' },
        status: driver?.status ?? 'Unknown',
        statusKey: getDriverStatusLabelKey(driver?.status ?? 'Unknown'),
        startTime: driver?.startTime,
        yardCheckInThreshold: driver?.yardCheckInThreshold,
        vehicleInYardThreshold: driver?.vehicleInYardThreshold
      })) ?? [];
      this.filteredDriversData = this.drivers;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
          callback: () => {
            this.isLoading = false;
          }
        }
      );
    }
  }

  filterDrivers(event: Event): void {
    const search = (event.target as HTMLInputElement).value;

    if (!search || search.trim() === '') {
      this.paginatedDateFilterRequest.page = 1;
      this.paginatedDateFilterRequest.searchTerm = search;
      this.getDriverStatusDetailAsync();
      return;
    }
    this.paginatedDateFilterRequest.searchTerm = search;
    this.getDriverStatusDetailAsync();

    this.filteredDriversData = this.drivers.filter((driver: any) =>
      driver.name.toLowerCase().includes(search.toLowerCase()) ||
      driver.vehicle.toLowerCase().includes(search.toLowerCase()) ||
      driver.status.toLowerCase().includes(search.toLowerCase())
    );
  }

  public getStatusClass(status: string): string {
    return `bg-${status}`;
  }

  public getNumbers(): number[] {
    return this.numberOfPages > 0 ? Array.from({ length: this.numberOfPages }, (_, i) => i + 1) : [];
  }
}